import { useEffect, useState } from "react";
import CreateAdModal from "./createAdModal";
import { collection, deleteDoc, doc, getDocs, Timestamp } from "firebase/firestore";
import { db } from "../../../../firebase";
import CompleteAdModal from "./completeAdModal";
import AdminPage from "../../../core/admin/adminPage";
import AdminCard from "../../../core/admin/adminCard";
import AdminTabs from "../../../core/admin/adminTabs";
import AdminButton from "../../../core/admin/adminButton";
import AdminBadge from "../../../core/admin/adminBadge";
import AdminConfirm from "../../../core/admin/adminConfirm";
import AdminEmptyState from "../../../core/admin/adminEmptyState";
import { AdminLoading } from "../../../core/admin/adminSpinner";
import { ImageIcon, MegaphoneIcon, PlusLineIcon, TrashIcon } from "../../../../img/svg/adminIcons";
import { deleteObject, getStorage, listAll, ref } from "firebase/storage";
import { parseFirebaseTimestamp } from "../../../../utils/functions";

export interface IAd {
    id: string;
    createdAt: Timestamp;
    until: Timestamp;
    link: string;
    imgUrl: string;
    mImgUrl: string;
    name: string;
    visible: boolean;
    order: number;
    draft: boolean;
}

export default function AdminAds() {

    const [createModal, setCreateModal] = useState(false);
    const [completeModal, setCompleteModal] = useState(false);
    const [ads, setAds] = useState<IAd[]>([]);
    const [loading, setLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState<number | null>(null);
    const [editableAd, setEditableAd] = useState<IAd | null>(null);
    const [deletion, setDeletion] = useState<number | null>(null);
    const [tab, setTab] = useState(0);

    useEffect(() => {
        doGetAds();
    }, [])

    const doGetAds = async () => {
        try {
            setLoading(true);
            setCreateModal(false);
            const docs: { id: string; until: Timestamp, link: string, imgUrl: string, mImgUrl: string, name: string, visible: boolean, order: number, draft: boolean, createdAt: Timestamp }[] = [];
            const docsSnap = await getDocs(collection(db, "ads"));
            if (docsSnap.empty) {
                setAds([]);
                setLoading(false);
                return null;
            }
            docsSnap.forEach(document => {
                const data = document.data();
                docs.push({
                    id: document.id,
                    createdAt: data.createdAt,
                    until: data.until,
                    link: data.link,
                    imgUrl: data.imgUrl,
                    mImgUrl: data.mImgUrl,
                    name: data.name,
                    visible: data.visible,
                    order: data.order,
                    draft: data.draft
                })
            })
            setAds(docs);
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    }

    const handleCompleteModal = async (ad: IAd) => {
        setEditableAd(ad);
        setCompleteModal(true);
    }

    const handleDeletion = async (ad: IAd, idx: number) => {
        try {
            setDeleteLoading(idx);
            setDeletion(null);
            if (!ad.id) {
                setDeleteLoading(null);
                return null;
            }
            // excluir imagens do relógio
            const storage = getStorage();
            const folderRef = ref(storage, `ads/${ad?.id}`);
            const result = await listAll(folderRef);

            const deletePromises = result.items.map((itemRef) => {
                return deleteObject(itemRef);
            })

            await Promise.all(deletePromises);

            // excluir documento do relógio
            const docRef = doc(db, "ads", ad.id);
            await deleteDoc(docRef);

        } catch (error) {
            console.log(error);
        } finally {
            setDeleteLoading(null);
            doGetAds();
        }
    }

    const now = Timestamp.now();
    const displayedAds = ads.filter(a => a.visible && a.until > now);
    const otherAds = ads.filter(a => !a.visible || a.until < now);
    const tabAds = ads?.filter(a => tab === 0 ? a.visible && a.until > now : !a.visible || a.until < now);

    return (
        <AdminPage
            title="Anúncios"
            description="Publicidade de terceiros exibida na home. Um anúncio só aparece enquanto estiver visível e dentro da data final."
            actions={<AdminButton icon={<PlusLineIcon width="16px" />} onClick={() => setCreateModal(true)}>Criar anúncio</AdminButton>}
        >
            <AdminCard
                bodyClassName="p-3 md:p-4"
                header={
                    <AdminTabs
                        value={tab}
                        onChange={setTab}
                        tabs={[
                            { value: 0, label: "Em exibição", count: loading ? undefined : displayedAds.length },
                            { value: 1, label: "Outros", count: loading ? undefined : otherAds.length },
                        ]}
                    />
                }
            >
                {
                    loading ?
                        <AdminLoading />
                        : tabAds.length ?
                            <ul className="flex flex-col gap-3">
                                {
                                    tabAds.map((ad, idx) => {
                                        const expired = ad.until < now;
                                        return (
                                            <li key={ad.id || idx} className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border border-gray-200 p-3">
                                                <div className="h-14 w-full sm:w-40 shrink-0 overflow-hidden rounded-lg bg-gray-900 flex items-center justify-center text-gray-500">
                                                    {
                                                        ad.imgUrl ?
                                                            <img src={ad.imgUrl} alt={ad.name} className="h-full w-full object-cover" />
                                                            : <ImageIcon />
                                                    }
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <p className="font-bold text-gray-900 break-words">{ad.name || "Sem nome"}</p>
                                                    <p className="text-sm text-gray-500">Visível até <strong className="text-gray-700">{parseFirebaseTimestamp(ad.until)}</strong></p>
                                                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                                                        {
                                                            ad.visible ?
                                                                <AdminBadge tone="success" dot>Visível</AdminBadge>
                                                                : <AdminBadge tone="neutral" dot>Oculto</AdminBadge>
                                                        }
                                                        {
                                                            expired ?
                                                                <AdminBadge tone="danger">Expirado</AdminBadge>
                                                                : null
                                                        }
                                                        {
                                                            !ad.imgUrl || !ad.mImgUrl ?
                                                                <AdminBadge tone="warning">Imagens pendentes</AdminBadge>
                                                                : null
                                                        }
                                                    </div>
                                                </div>
                                                {
                                                    deletion === idx ?
                                                        <AdminConfirm message="Excluir anúncio?" confirmLabel="Excluir" cancelLabel="Cancelar" danger onConfirm={() => handleDeletion(ad, idx)} onCancel={() => setDeletion(null)} />
                                                        :
                                                        <div className="flex items-center gap-2 [&>*]:flex-1 sm:[&>*]:flex-none">
                                                            <AdminButton size="sm" variant="secondary" disabled={deleteLoading !== null} onClick={() => handleCompleteModal(ad)}>Ver</AdminButton>
                                                            <AdminButton size="sm" variant="ghost" className="!text-red-600 hover:!bg-red-50" title="Excluir" loading={deleteLoading === idx} disabled={deleteLoading !== null} onClick={() => setDeletion(idx)} icon={<TrashIcon width="16px" />}>Excluir</AdminButton>
                                                        </div>
                                                }
                                            </li>
                                        )
                                    })
                                }
                            </ul>
                            :
                            <AdminEmptyState icon={<MegaphoneIcon />} title={tab === 0 ? "Nenhum anúncio em exibição" : "Nenhum outro anúncio"} description={tab === 0 ? "Anúncios ocultos ou expirados ficam na aba “Outros”." : undefined} />
                }
            </AdminCard>
            {
                createModal ?
                    <CreateAdModal close={() => setCreateModal(false)} finish={doGetAds} /> :
                    null
            }
            {
                completeModal && editableAd ?
                    <CompleteAdModal refresh={doGetAds} close={() => setCompleteModal(false)} setEditableAd={setEditableAd} editableAd={editableAd} />
                    : null
            }
        </AdminPage>
    )
}
